import { useQuery, useQueryClient } from "@tanstack/react-query";
import { activityApi, type ActivityEvent } from "../api/activity";
import type { PagedResult } from "@/types/api";

/**
 * Activity Feed Aggregator v1.0 (PR 4) — TanStack Query hooks for the
 * cross-module Activity tab. Cache is keyed by `(ownerType, ownerId)`
 * plus the visible window so source-module mutations (post a note, log a
 * call, finish a task) can invalidate the whole tree at once via
 * `invalidateActivityFor`.
 */
export const activityKeys = {
  all: ["activity"] as const,
  byOwner: (ownerType: string, ownerId: string) =>
    [...activityKeys.all, "by-owner", ownerType, ownerId] as const,
  list: (
    ownerType: string,
    ownerId: string,
    since: string | undefined,
    skip: number,
    take: number,
  ) =>
    [
      ...activityKeys.byOwner(ownerType, ownerId),
      "list",
      since ?? null,
      skip,
      take,
    ] as const,
  recentCount: (ownerType: string, ownerId: string) =>
    [...activityKeys.byOwner(ownerType, ownerId), "recent-count"] as const,
};

export interface UseActivityByOwnerOptions {
  since?: string;
  skip?: number;
  take?: number;
  enabled?: boolean;
}

export function useActivityByOwner(
  ownerType: string,
  ownerId: string,
  options: UseActivityByOwnerOptions = {},
) {
  const { since, skip = 0, take = 50, enabled = true } = options;
  return useQuery<PagedResult<ActivityEvent>>({
    queryKey: activityKeys.list(ownerType, ownerId, since, skip, take),
    queryFn: () =>
      activityApi.listByOwner(ownerType, ownerId, { since, skip, take }),
    enabled: enabled && Boolean(ownerType) && Boolean(ownerId),
  });
}

export function useActivityRecentCount(
  ownerType: string,
  ownerId: string,
  windowDays = 7,
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options;
  return useQuery<number>({
    queryKey: [...activityKeys.recentCount(ownerType, ownerId), windowDays],
    queryFn: () => activityApi.recentCount(ownerType, ownerId, windowDays),
    enabled: enabled && Boolean(ownerType) && Boolean(ownerId),
  });
}

/**
 * Cross-module invalidation seam. Source modules (Notes, Tasks,
 * Communications, Messaging) call this from their mutation hooks so a
 * posted note or completed task immediately refreshes the Activity tab
 * and its badge — independent of the 30-second backend cache TTL.
 */
export function invalidateActivityFor(
  qc: ReturnType<typeof useQueryClient>,
  ownerType: string,
  ownerId: string,
) {
  void qc.invalidateQueries({
    queryKey: activityKeys.byOwner(ownerType, ownerId),
  });
}
